import { Card, CardContent, CardHeader } from '@/components/ui';
import type { ErrorListItem } from '@/types/api';

import { ResolutionPanel } from '../ErrorDetail';
import { GuidancePanel } from './GuidancePanel';
import { KGContextPanel } from './KGContextPanel';

/** P / T / V / M errors share the same 3-panel layout (Spec S08 §"Layout 3 panneaux"). */
export function StandardReviewLayout({ error }: { error: ErrorListItem }) {
  const payload = error.payload as Record<string, unknown>;
  // `source_text` is populated by the detail endpoint (snippet around
  // the subject IRI's local name); `source_text_span` is the legacy
  // path the agents never actually wrote. Keep the legacy lookup as a
  // last-resort fallback so older error rows don't regress.
  const sourceText =
    error.source_text ?? (payload.source_text_span as string | undefined) ?? null;

  return (
    <div className="grid h-full grid-cols-[31%_38%_31%] gap-3 p-3">
      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">Source text</h3>
          {error.source_url && (
            <a
              href={error.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate text-small text-accent-700 hover:text-accent"
              title={error.source_url}
            >
              {error.source_url}
            </a>
          )}
        </CardHeader>
        <CardContent className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-small text-slate-700">
          {sourceText ?? <span className="text-slate-400">—</span>}
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <h3 className="text-h3 text-brand-900">Knowledge graph context</h3>
        </CardHeader>
        <CardContent className="p-0">
          <KGContextPanel
            highlightedSubject={
              (error.payload as Record<string, unknown>).subject_uri as string | undefined
            }
            hops={1}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">Resolution</h3>
        </CardHeader>
        <CardContent>
          {error.guidance && <GuidancePanel guidance={error.guidance} />}
          <PayloadSummary error={error} />
          <hr className="my-3 border-slate-200" />
          <ResolutionPanel
            error={error}
            availableActions={['fix_and_accept', 'cancel', 'escalate']}
            decisionTypes={{
              fix_and_accept: ['fix_and_accept'],
              cancel: ['cancel'],
              escalate: ['skill_escalation'],
              suspend: ['deferred'],
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function PayloadSummary({ error }: { error: ErrorListItem }) {
  const payload = error.payload as Record<string, unknown>;
  return (
    <dl className="grid grid-cols-2 gap-y-1 text-small">
      <Row label="Subject" value={String(payload.subject_uri ?? '—')} />
      <Row label="Class" value={String(payload.subject_class ?? '—')} />
      <Row label="Predicate" value={String(payload.predicate_uri ?? '—')} />
      <Row label="Value" value={String(payload.object_value ?? '—')} />
      <Row label="Check" value={String(payload.check_id ?? error.error_subtype ?? '—')} />
    </dl>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="truncate font-mono text-slate-700" title={value}>
        {value}
      </dd>
    </>
  );
}
